import { useEffect, useState } from "react";
import { BONUS_HPR_MARKER, BONUS_PERCENT_MARKER } from "../utils/constants";
import {
  FiatCurrency,
  PurchasePlan,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { handleGetPurchasePlans } from "../api-clients/walletsPlansClient";

export type MetadataObj = Record<string, Record<string, number>>;

export default function usePurchasePlans(currency?: FiatCurrency) {
  const [purchasePlans, setPurchasePlans] = useState<PurchasePlan[]>();
  const [welcomeInsurancePlans, setWelcomeInsurancePlans] =
    useState<PurchasePlan[]>();
  const [plansMetadataObj, setPlansMetadataObj] = useState<MetadataObj>({});
  const [bestDealId, setBestDealId] = useState<PurchasePlan["id"]>("");

  // This was done to optimize the metadata lookup in the TopupBundle component
  const generateMetadataObj = (plans: PurchasePlan[]) => {
    let result: MetadataObj = {};
    plans.forEach(({ id, metadata }, index) => {
      if (!metadata || metadata.length <= 0) return;
      const metadataObj = metadata.reduce(
        (prevValue, currValue) => ({
          ...prevValue,
          [currValue.name]: +currValue.value,
        }),
        { initialIndex: index }
      );
      result[id] = metadataObj;
    });
    return result;
  };

  const getBestDeal = (metadataObj: MetadataObj) => {
    let maxBonusPercent = 0;
    let bestDeal = { id: "", initialIndex: -1, bonusHprOffer: 0 };

    for (const planId in metadataObj) {
      if (!metadataObj.hasOwnProperty(planId)) continue;
      const initialIndex = metadataObj[planId].initialIndex;
      const bonusPercent = metadataObj[planId][BONUS_PERCENT_MARKER];
      const bonusHprOffer = metadataObj[planId][BONUS_HPR_MARKER];
      if (
        bonusPercent > maxBonusPercent ||
        (bonusPercent === maxBonusPercent &&
          bonusHprOffer > bestDeal.bonusHprOffer)
      ) {
        maxBonusPercent = bonusPercent;
        bestDeal = { id: planId, initialIndex, bonusHprOffer };
      }
    }
    return bestDeal;
  };

  useEffect(() => {
    (async () => {
      const purchasePlansData = await handleGetPurchasePlans(currency);
      if (!purchasePlansData) return;

      const { allPlans, specialOfferPlans } = purchasePlansData;
      const metadataObj = generateMetadataObj(allPlans);
      const bestDeal = getBestDeal(metadataObj);
      setPlansMetadataObj(metadataObj);
      setWelcomeInsurancePlans(specialOfferPlans);
      if (!bestDeal.id) {
        setPurchasePlans(allPlans);
        return;
      }

      const bestDealPlan = allPlans[bestDeal.initialIndex];
      setBestDealId(bestDeal.id);
      setPurchasePlans([
        bestDealPlan,
        ...allPlans.filter((plan) => plan.id !== bestDeal.id),
      ]);
    })();
  }, [currency]);

  return {
    purchasePlans,
    welcomeInsurancePlans,
    plansMetadataObj,
    bestDealId,
  };
}
